import { isValid, parseISO } from "date-fns";

/**
 * Safari throws `RangeError: date value is not finite in DateTimeFormat format()`
 * when Intl formats an Invalid Date, and also for some `hour12: false` options.
 * Every displayed timestamp should go through this module.
 */
export function parseDisplayDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }
  if (value == null) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const normalized = raw.replace(
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:[.,](\d+))?/,
    (_match, date: string, time: string, fraction?: string) => {
      const milliseconds = fraction
        ? `.${fraction.slice(0, 3).padEnd(3, "0")}`
        : "";
      return `${date}T${time}${milliseconds}`;
    },
  );

  const parsed = parseISO(normalized);
  if (isValid(parsed)) return parsed;

  const native = new Date(normalized);
  return Number.isFinite(native.getTime()) ? native : null;
}

const safariSafeOptions = (
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions | undefined => {
  if (!options || options.hour12 !== false) return options;
  const { hour12: _hour12, ...rest } = options;
  return { ...rest, hourCycle: options.hourCycle ?? "h23" };
};

export function formatDisplayDate(
  value: unknown,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
  fallback?: string,
): string {
  const rawFallback =
    fallback ??
    (typeof value === "string" ? value : value == null ? "" : "");
  const date = parseDisplayDate(value);
  if (!date) return rawFallback;

  try {
    return new Intl.DateTimeFormat(locales, safariSafeOptions(options)).format(
      date,
    );
  } catch {
    try {
      return new Intl.DateTimeFormat(locales).format(date);
    } catch {
      return rawFallback;
    }
  }
}
